import { Fragment, useEffect } from '@wordpress/element';
import { dispatch, select } from '@wordpress/data';
import { BaseControl, Button, PanelBody, PanelRow, SelectControl, FocalPointPicker, RangeControl, ToggleControl, TextControl } from '@wordpress/components';
import { useBlockProps, InspectorControls, InnerBlocks, MediaUpload, MediaUploadCheck, ColorPalette, __experimentalGradientPicker as GradientPicker } from '@wordpress/block-editor';
import { __ } from '@wordpress/i18n';
import { registerBlockType, createBlock } from '@wordpress/blocks';
import { createHigherOrderComponent } from '@wordpress/compose';
import { addFilter } from '@wordpress/hooks';

const ENABLE_ON_BLOCKS = [
	'superblockslider/slide'
]

const ALLOWED_MEDIA_TYPES = [ 'image' ];

registerBlockType( 'superblockslider/slide', {
	apiVersion: 2,
	category: 'media',
	title: __( 'Single slide' ),
	icon: 'embed-photo',
    parent: [ 'superblockslider/slider' ],
    description: __( 'Single slide for Super slider block' ),
	usesContext: [
		'superblockslider/slider/initialActiveSlideValue',
		'superblockslider/slider/transitionEffectValue',
		'superblockslider/slider/transitionSpeedValue',
		'superblockslider/slider/transitionAnimationValue',
	],
	attributes: {
		slideTitle: {
			type: 'string',
			default: 'Title'
		},
		isActiveSlide: {
			type: 'boolean',
			default: false,
		},
		slideIndex: {
			type: 'integer',
			default: 0,
		},
		backgroundImage: {
			type: 'object',
			default: {},
		},
		backgroundColorValue: {
			type: 'string',
			default: '',
		},
		backgroundGradientValue: {
			type: 'string',
			default: '',
		},
		backgroundFixedValue: {
			type: 'boolean',
			default: false,
		},
		backgroundSizeValue: {
			type: 'string',
			default: 'cover',
		},
		backgroundRepeatValue: {
			type: 'string',
			default: 'no-repeat',
		},
		overlayOpacityValue: {
			type: 'integer',
			default: 75,
		},
		focalPoint: {
			type: 'object',
			default: {
				x: 0.5,
				y: 0.5,
			},
		},
		contentVerticalAlignValue: {
			type: 'string',
			default: '',
		},
		lazyloadValue: {
			type: 'boolean',
			default: false,
		},
		transitionDuration: {
			type: 'string',
			default: '',
		},
		transitionTimingFunction: {
			type: 'string',
			default: '',
		},
		slideLinkValue: {
			type: 'string',
			default: '',
		},
		slideTargetValue: {
			type: 'boolean',
			default: false,
		},
		parallaxValue: {
			type: 'boolean',
			default: false,
		},
		parallaxSpeedValue: {
			type: 'integer',
			default: 50,
		},
	},
	edit: (props) => {
		const {
			attributes: {
				slideIndex,
				isActiveSlide,
				focalPoint,
				backgroundImage,
				backgroundColorValue,
				backgroundGradientValue,
				backgroundFixedValue,
				backgroundSizeValue,
				backgroundRepeatValue,
				contentVerticalAlignValue,
				overlayOpacityValue,
				lazyloadValue,
				transitionDuration,
				transitionTimingFunction,
				slideLinkValue,
				slideTargetValue,
				parallaxValue,
				parallaxSpeedValue,
			},
			setAttributes,
			clientId,
			className,
			context,
		} = props;
		
		const focalPointOnChange = (points)=> {
			setAttributes({focalPoint: points })
		}

		const backgroundImageOnSelect = (backgroundImageNew) => {
			setAttributes({ backgroundImage: backgroundImageNew})
		}
		const backgroundFixedValueOnChange = (backgroundFixedValueNew) => {
			setAttributes({ backgroundFixedValue: backgroundFixedValueNew})
		}
		const backgroundSizeValueOnChange = (backgroundSizeValueNew) => {
			setAttributes({ backgroundSizeValue: backgroundSizeValueNew})
		}
		const backgroundRepeatValueOnChange = (backgroundRepeatValueNew) => {
			setAttributes({ backgroundRepeatValue: backgroundRepeatValueNew})
		}
		const contentVerticalAlignValueOnChange = (contentVerticalAlignValueNew) => {
			setAttributes({ contentVerticalAlignValue: contentVerticalAlignValueNew})
		}
		const overlayOpacityValueOnChange = (overlayOpacityValueNew) => {
			setAttributes({ overlayOpacityValue: overlayOpacityValueNew})
		}
		const lazyloadValueOnChange = (lazyloadValueNew) => {
			setAttributes({ lazyloadValue: lazyloadValueNew})
		}
		const backgroundColorValueOnChange = (backgroundColorValueNew) => {
			if(backgroundGradientValue) {
				setAttributes({ backgroundGradientValue: ''})
			}
			setAttributes({ backgroundColorValue: backgroundColorValueNew})
		}
		const backgroundGradientValueOnChange = (backgroundGradientValueNew) => {
			if(backgroundGradientValue) {
				setAttributes({ backgroundColorValue: ''})
			}
			setAttributes({ backgroundGradientValue: backgroundGradientValueNew})
		}

		const parallaxValueOnChange = (parallaxValueNew) => {
			setAttributes({ parallaxValue: parallaxValueNew})
		}
		const parallaxSpeedValueOnChange = (parallaxSpeedValueNew) => {
			setAttributes({ parallaxSpeedValue: parallaxSpeedValueNew})
		}

		const backgroundImageRemoveOnClick = () => {
			setAttributes({ backgroundImage: {} })
		}

		/**
		 * remove slide click
		 * @param {event} event 
		 */
		 const removeSlideButtonOnClick = (event) => {
			event.stopPropagation();

			const parent = select('core/block-editor').getBlockRootClientId(clientId)
			const rootBlock = select('core/block-editor').getBlock(parent)
			dispatch('core/block-editor').removeBlock(rootBlock.innerBlocks[slideIndex].clientId)
		}
		const addSlideButtonOnClick = (event) => {
			const parent = select('core/block-editor').getBlockRootClientId(clientId)
			const rootBlock = select('core/block-editor').getBlock(parent)

			const newSlide = createBlock('superblockslider/slide', {})
			// console.log(rootBlock.innerBlocks.length)
			dispatch('core/block-editor').insertBlock(newSlide, rootBlock.innerBlocks.length, rootBlock.clientId)
			// .then((data)=> {
			// 	dispatch('core/block-editor').selectBlock(data.blocks[0].clientId);
			// })
		}
		const sliderSettingsOnClick = (event) => {
			const parent = select('core/block-editor').getBlockRootClientId(clientId)
			const rootBlock = select('core/block-editor').getBlock(parent)
			dispatch('core/block-editor').selectBlock(rootBlock.clientId);
		}
		const slideLinkValueOnChange = (slideLinkValueNew) => {
			setAttributes({ slideLinkValue: slideLinkValueNew })
		}
		const slideTargetValueOnChange = (slideTargetValueNew) => {
			setAttributes({ slideTargetValue: slideTargetValueNew })
		}
		
		let overlayStyle = {
		}
		if(backgroundColorValue) {
			overlayStyle = { ...overlayStyle, 
				backgroundColor: backgroundColorValue,
				opacity: overlayOpacityValue / 100,
			}
		}
		if(backgroundGradientValue) {
			overlayStyle = { ...overlayStyle, 
				backgroundImage: backgroundGradientValue,
				opacity: overlayOpacityValue / 100,
			}
		}
		
		let slideImageStyle = {}
		let backgroundSrcset = ''
		let backgroundSizes = ''

		let lazyloadType = 'eager'
		if(backgroundImage.url) {
			slideImageStyle = { ...slideImageStyle,
				objectPosition: `${ focalPoint.x * 100 }% ${ focalPoint.y * 100 }%`,
				objectFit: backgroundSizeValue,
			}
			if(backgroundFixedValue) {
				slideImageStyle = { ...slideImageStyle, backgroundAttachment: 'fixed'}
			}
			
			// lazyload
			if(lazyloadValue) {
				lazyloadType = 'lazy'
			}

			for (const size in backgroundImage.sizes) {
				backgroundSrcset += `${backgroundImage.sizes[size].url} ${backgroundImage.sizes[size].width}w,`
			}
			
			backgroundSizes = `(max-width: ${backgroundImage.width}px) 100vw, ${backgroundImage.width}px`
		}

		const dimensions = {
			width: 400,
			height: 100,
		};

		let slideClass = ['superblockslider__slide']
		
		// context['superblockslider/slider/initialActiveSlideValue'] == slideIndex ? slideClass.push('superblockslider__slide--active') : null
		if (context['superblockslider/slider/initialActiveSlideValue'] == slideIndex && isActiveSlide == false) {
			setAttributes({isActiveSlide: true})
		}
		else if(context['superblockslider/slider/initialActiveSlideValue'] != slideIndex && isActiveSlide == true) {
			setAttributes({isActiveSlide: false})
		}
		isActiveSlide ? slideClass.push('superblockslider__slide--active') : null

		contentVerticalAlignValue ? slideClass.push(contentVerticalAlignValue) : null
		
		slideClass = slideClass.filter(Boolean).join(' ')
		
		let innerClass = ['superblockslider__slide_inner']

        const blockProps = useBlockProps()
		blockProps.className += ' ' + slideClass
		
		let slideStyle = {}
		if(context['superblockslider/slider/transitionEffectValue'] == 'fade') {
			setAttributes({ transitionDuration : context['superblockslider/slider/transitionSpeedValue']})
			setAttributes({ transitionTimingFunction : context['superblockslider/slider/transitionAnimationValue']})

			slideStyle = {
				transitionDuration: transitionDuration,
				transitionTimingFunction: transitionTimingFunction,
			}
		}
		else {
			setAttributes({ transitionDuration : ''})
			setAttributes({ transitionTimingFunction : ''})
		}

		return [
			<div {...blockProps} data-slide-index={slideIndex} style={slideStyle} >

				<button onClick={removeSlideButtonOnClick} data-slide-id={slideIndex} type="button" className="components-button block-list-appender__toggle block-editor-button-remove superblockslider__button--remove" title="Delete Slide">Delete<span class="dashicons dashicons-no-alt"></span></button>
				
				<div className='superblockslider__slide__bg'>
					{ backgroundImage.url && 
						<img src={backgroundImage.url} width={backgroundImage.width} height={backgroundImage.height} title={backgroundImage.title} alt={backgroundImage.alt}
							srcSet={backgroundSrcset}
							sizes={backgroundSizes}
							style={slideImageStyle}
							loading={lazyloadType}
						/>
					}
				</div>

				<div className='superblockslider__slide__overlay' style={overlayStyle}>
				</div>
				
				<div className={innerClass}>
					<InnerBlocks templateLock={false} />
				</div>
				
			</div>,

            <InspectorControls>
				<PanelBody>
					
					<PanelRow>
						<Button isSecondary={true} onClick={sliderSettingsOnClick} label="Slider settings" icon='slides'>
							Edit slider settings
						</Button>
					</PanelRow>

					{/* <PanelRow>

						<Button isSecondary={true} onClick={addSlideButtonOnClick} label="Add new slide" icon='table-row-after'>
							Add new slide
						</Button>
					</PanelRow> */}

				</PanelBody>

				
				<PanelBody title={__('Single slide settings')} initialOpen={true} >

					{ backgroundImage.url &&
						<BaseControl
						label="Set focus"
						>

							<FocalPointPicker
								url={ backgroundImage.url }
								dimensions={ dimensions }
								value={ focalPoint }
								onChange={ ( focalPoint ) => {
									focalPointOnChange(focalPoint)
								} }
							/>

						</BaseControl>
					}

					<MediaUploadCheck>
						<MediaUpload
							onSelect={ ( media ) => console.log( 'selected ' + media.length ) }
							allowedTypes={ ALLOWED_MEDIA_TYPES }
							// value={ 7533 }
							onSelect={backgroundImageOnSelect}
							render={ ( { open } ) => {
								return !backgroundImage.url ?
								<PanelRow>
									
									<Button onClick={ open } isPrimary={true}>
										Set Background Image
									</Button>
								</PanelRow>
									
								:
								<Fragment>

								{/* <PanelRow>
									<img src={backgroundImage.url} />
								</PanelRow> */}
								<PanelRow>
									<Button onClick={ backgroundImageRemoveOnClick } isSecondary={true} isSmall={true}>
										Remove background
									</Button>
								</PanelRow>
								</Fragment>

							} }
						/>
					</MediaUploadCheck>


					{ backgroundImage.url &&
						<Fragment>

							<SelectControl
								label={__('Background size')}
								help='Background can be left to its natural size, stretched, or constrained to fit the available space.'
								value={backgroundSizeValue}
								options={[
									{ label: 'Auto', value: 'none' },
									{ label: 'Contain (constrained)', value: 'contain' },
									{ label: 'Cover (stretched)', value: 'cover' },
									{ label: 'Fill (stretched + constrained)', value: 'fill' },
								]}
								onChange={backgroundSizeValueOnChange}
							/>
							<ToggleControl
								label='Enable lazyload'
								help="Start loading background image when visible on screen. (Recommend keeping this disabled)"
								checked={lazyloadValue}
								onChange={lazyloadValueOnChange}
							/>

							{/* <SelectControl
								label={__('Background repeat')}
								value={backgroundRepeatValue}
								options={[
									{ label: 'No repeat', value: 'no-repeat' },
									{ label: 'Repeat', value: 'Repeat' },
									{ label: 'Repeat horizontally', value: 'repeat-x' },
									{ label: 'Repeat vertically', value: 'repeat-y' },
								]}
								onChange={backgroundRepeatValueOnChange}
							/> */}

							{/* <ToggleControl
								label='Fixed background'
								help="Fixed background within the slide."
								checked={backgroundFixedValue}
								onChange={backgroundFixedValueOnChange}
							/> */}


						</Fragment>
					}

					<PanelRow>
						<SelectControl
							label='Vertical alignment content'
							value={ contentVerticalAlignValue } 
							onChange={ contentVerticalAlignValueOnChange }
							options={[
								{ label: 'Default', value: '' },
								{ label: 'Center', value: 'is-vertical-center' },
								{ label: 'Bottom', value: 'is-vertical-bottom' },
							]}
						/>
					</PanelRow>
					
				</PanelBody>
				
				<PanelBody title={__('Parallax BG')} initialOpen={parallaxValue ? true : false} >

					<PanelRow>
						<p className='small'>Add parallax effect to background image
						<br/>
						Note: No preview while in editor.</p>
					</PanelRow>

					<PanelRow>
						<ToggleControl
							label='Enable parallax'
							help='Setting "Background size" to "Cover" is recommended in parallax mode.'
							checked={parallaxValue}
							onChange={parallaxValueOnChange}
						/>
					</PanelRow>

					<PanelRow>
						<RangeControl
							label='Speed'
							help='Note: Background image will scale based on the speed to fit.'
							value={parallaxSpeedValue}
							onChange={parallaxSpeedValueOnChange}
							max={ 100 }
						/>
					</PanelRow>
				</PanelBody>

				<PanelBody title={__('Background overlay')} initialOpen={(backgroundColorValue || backgroundGradientValue) ? true : false} >
					<BaseControl
						label='Solid background color'
					>
						<ColorPalette
							value={ backgroundColorValue }
							onChange={ backgroundColorValueOnChange }
						/>
					</BaseControl>
					
					<BaseControl
						label='Gradient background color'
					>
						<GradientPicker
							value={backgroundGradientValue}
							onChange={backgroundGradientValueOnChange}
						/>
					</BaseControl>

					<RangeControl
						label='Overlay opacity'
						value={overlayOpacityValue}
						onChange={overlayOpacityValueOnChange}
        				max={ 100 }
					/>
				</PanelBody>

				<PanelBody title={__('Slide Link')} initialOpen={slideLinkValue ? true : false} >
					<PanelRow>
						<p className='small'>Add URL link to slide.
						<br/>
						Note: No preview while in editor.</p>
					</PanelRow>

					<PanelRow>
						<TextControl 
							label={__('URL')}
							help={__('e.g. https://google.com/')}
							type='text'
							value={slideLinkValue}
							onChange={slideLinkValueOnChange}
						/>
					</PanelRow>

					<PanelRow>
						<ToggleControl
							label='Open in new tab'
							checked={slideTargetValue}
							onChange={slideTargetValueOnChange}
						/>
					</PanelRow>
				</PanelBody>

            </InspectorControls>
		];
	},

	save: ( props ) => {
		const {
			attributes: {
				slideIndex,
				isActiveSlide,
				backgroundImage,
				backgroundColorValue,
				backgroundGradientValue,
				backgroundFixedValue,
				backgroundSizeValue,
				backgroundRepeatValue,
				focalPoint,
				contentVerticalAlignValue,
				overlayOpacityValue,
				lazyloadValue,
				transitionDuration,
				transitionTimingFunction,
				slideLinkValue,
				slideTargetValue,
				parallaxValue,
				parallaxSpeedValue,
			},
		} = props;
		
		let overlayStyle = {
		}
		if(backgroundColorValue) {
			overlayStyle = { ...overlayStyle, 
				backgroundColor: backgroundColorValue,
				opacity: overlayOpacityValue / 100,
			}
		}
		if(backgroundGradientValue) {
			overlayStyle = { ...overlayStyle, 
				backgroundImage: backgroundGradientValue,
				opacity: overlayOpacityValue / 100,
			}
		}
		let slideImageStyle = {}
		let backgroundSrcset = ''
		let backgroundSizes = ''

		let lazyloadType = 'eager'
		if(backgroundImage.url) {
			slideImageStyle = { ...slideImageStyle,
				objectPosition: `${ focalPoint.x * 100 }% ${ focalPoint.y * 100 }%`,
				objectFit: backgroundSizeValue,
			}
			if(backgroundFixedValue) {
				slideImageStyle = { ...slideImageStyle, backgroundAttachment: 'fixed'}
			}
			// lazyload
			if(lazyloadValue) {
				lazyloadType = 'lazy'
			}
			// parallax
			if(parallaxValue) {
				slideImageStyle = { ...slideImageStyle, height: `${100 + parallaxSpeedValue}%`}
				
			}

			for (const size in backgroundImage.sizes) {
				backgroundSrcset += `${backgroundImage.sizes[size].url} ${backgroundImage.sizes[size].width}w,`
			}
			
			backgroundSizes = `(max-width: ${backgroundImage.width}px) 100vw, ${backgroundImage.width}px`
		}

		let slideClass = ['superblockslider__slide']
		
		isActiveSlide ? slideClass.push('superblockslider__slide--active') : null
		contentVerticalAlignValue ? slideClass.push(contentVerticalAlignValue) : null
		
		slideClass = slideClass.filter(Boolean).join(' ')
		
		let innerClass = ['superblockslider__slide_inner']
		innerClass = innerClass.filter(Boolean).join(' ')

		let slideStyle = {
			
		}

		if(transitionDuration && transitionTimingFunction) {
			slideStyle = {
				style: {
					transitionDuration: transitionDuration,
					transitionTimingFunction: transitionTimingFunction,
				}
			}
		}

		let parallaxData = {}
		if(parallaxValue) {
			parallaxData = {
				'data-parallax' : true,
				'data-parallax-speed' : parallaxSpeedValue,
			}
		}

		let slidelinkOutput = ''
		if(slideLinkValue) {
			let slideTargetOutput = {}
			if(slideTargetValue) {
				slideTargetOutput = {
					target : '_new'
				}
			}
			slidelinkOutput = <a className='superblockslider__slide__link' href={slideLinkValue} {...slideTargetOutput} rel="noopener"></a>
		}

		return (
        <div className={ slideClass } data-slide-index={slideIndex} {...slideStyle} {...parallaxData}>

			<div className='superblockslider__slide__bg'>
				{ backgroundImage.url && 
					<img src={backgroundImage.url} width={backgroundImage.width} height={backgroundImage.height} title={backgroundImage.title} alt={backgroundImage.alt}
						loading={lazyloadType}
						srcSet={backgroundSrcset}
						sizes={backgroundSizes}
						style={slideImageStyle}
					/>
				}
			</div>

			<div className='superblockslider__slide__overlay' style={overlayStyle}>
			</div>

			<div className={innerClass}>
				<InnerBlocks.Content />
			</div>

			{slidelinkOutput}
		</div>
        );
	}
});


const superblockslider_slide_BlockListBlock = createHigherOrderComponent( ( BlockListBlock ) => {
    return ( props ) => {
		// Do nothing if it's another block than our defined ones.
		if ( ! ENABLE_ON_BLOCKS.includes( props.name ) ) {
			return (
				<BlockListBlock {...props} />
			);
		}
		const {
			attributes: {
			},
			setAttributes
		} = props;

		const slideParent = wp.data.select('core/block-editor').getBlockParents(props.clientId)
		const parentBlocks = wp.data.select('core/block-editor').getBlocks(slideParent)
		
		parentBlocks.forEach((slide, index) => {
			
			if(props.clientId == slide.clientId) {
				setAttributes({slideIndex: index})
			}
		})

		return (
				<BlockListBlock {...props} />
		)

    };
}, 'superblockslider_slide_BlockListBlock' );
addFilter( 'editor.BlockListBlock', 'superblockslider/slide/BlockListBlock', superblockslider_slide_BlockListBlock );